// Where web push can reach someone at all, and when it is fair to ask.
//
// Everything here is synchronous and side-effect free apart from reading
// localStorage, so the home card can decide without a network round-trip and
// without ever triggering the browser's own permission dialog.

import { isMiniapp } from 'modules/miniapp'

export type PushAvailability =
    // A normal browser tab or an installed PWA that can subscribe.
    | 'ok'
    // A Bale / Telegram / Eitaa webview. They expose no push API that reaches
    // the phone, and asking there would only produce a dialog that does nothing.
    | 'miniapp'
    // Safari on iPhone/iPad in a tab. Apple only delivers web push to a site
    // that was added to the home screen and opened from there.
    | 'ios_browser'
    // No service worker, PushManager or Notification (old browsers, in-app
    // browsers of other apps).
    | 'unsupported'

export const isIOS = (
    userAgent: string = navigator.userAgent,
    maxTouchPoints: number = navigator.maxTouchPoints || 0
): boolean =>
    /iphone|ipad|ipod/i.test(userAgent) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/macintosh/i.test(userAgent) && maxTouchPoints > 1)

export type DevicePlatform = 'android' | 'ios' | 'macos' | 'windows' | 'other'

/** Where the OS's own notification switches are, which is what a test failure points at. */
export const devicePlatform = (
    userAgent: string = navigator.userAgent,
    maxTouchPoints: number = navigator.maxTouchPoints || 0
): DevicePlatform => {
    if (/android/i.test(userAgent)) return 'android'
    if (isIOS(userAgent, maxTouchPoints)) return 'ios'
    if (/windows/i.test(userAgent)) return 'windows'
    if (/macintosh|mac os x/i.test(userAgent)) return 'macos'
    return 'other'
}

/** The browser's name as the OS lists it in notification settings. */
export const browserName = (userAgent: string = navigator.userAgent): string => {
    if (/edg(a|ios)?\//i.test(userAgent)) return 'Microsoft Edge'
    if (/samsungbrowser\//i.test(userAgent)) return 'Samsung Internet'
    if (/opr\//i.test(userAgent)) return 'Opera'
    if (/firefox\/|fxios\//i.test(userAgent)) return 'Firefox'
    if (/chrome\/|crios\//i.test(userAgent)) return 'Google Chrome'
    if (/safari\//i.test(userAgent)) return 'Safari'
    return 'مرورگر'
}

export const isStandalone = (): boolean => {
    try {
        if (window.matchMedia?.('(display-mode: standalone)').matches)
            return true
    } catch {
        /* very old webviews */
    }
    return (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export const pushAvailability = (): PushAvailability => {
    if (isMiniapp()) return 'miniapp'
    // Checked before the API probe: iOS Safari in a tab has no PushManager, and
    // "add us to the home screen" is a much better answer than "unsupported".
    if (isIOS() && !isStandalone()) return 'ios_browser'
    if (
        !('serviceWorker' in navigator) ||
        !('PushManager' in window) ||
        !('Notification' in window)
    )
        return 'unsupported'
    return 'ok'
}

export const notificationPermission = (): NotificationPermission | null =>
    'Notification' in window ? Notification.permission : null

// ── asking, once ───────────────────────────────────────────────────────────
//
// The browser gives a site exactly one real permission dialog: a «Block»
// there is permanent unless the person digs through site settings. We used to
// put a card of our own in front of it on the home page; now the dialog itself
// is the ask, on the first open of the app, and it happens once — whatever it
// is answered, and including when it is closed without an answer. The record
// below is what keeps that promise across reloads and later visits.
//
// It is cleared on logout with the rest of localStorage, so the next person on
// a shared browser gets asked for themselves. Changing the answer afterwards is
// done from «اعلان‌ها» in the menu, which asks again and, for a browser that
// blocked us, says which setting to change.

const ASKED_KEY = 'webpush:asked'

export type AskAnswer = 'granted' | 'denied' | 'dismissed'

export interface AskRecord {
    at: number
    answer: AskAnswer
}

export const readAskRecord = (): AskRecord | null => {
    try {
        const raw = window.localStorage.getItem(ASKED_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return parsed?.answer
            ? { at: Number(parsed.at) || 0, answer: parsed.answer as AskAnswer }
            : null
    } catch {
        return null
    }
}

export const rememberAsked = (
    answer: AskAnswer,
    now: number = Date.now()
): AskRecord => {
    const record: AskRecord = { at: now, answer }
    try {
        window.localStorage.setItem(ASKED_KEY, JSON.stringify(record))
    } catch {
        /* best effort: worst case the dialog opens once more next visit */
    }
    return record
}

/**
 * Is this the moment to open the browser's dialog by ourselves?
 *
 * Pure on purpose: it decides without touching the network and without any
 * chance of triggering the dialog as a side effect.
 */
export const shouldAskOnFirstOpen = (
    availability: PushAvailability,
    permission: NotificationPermission | null,
    record: AskRecord | null
): boolean => availability === 'ok' && permission === 'default' && !record

// ── what this browser is subscribed as ─────────────────────────────────────
//
// Written only after the backend accepted the subscription. It lives in
// localStorage on purpose: logout and an expired session both clear
// localStorage, so a browser subscription found WITHOUT this record belongs to
// a session that is gone — see `syncWebPush`.

const STORED_KEY = 'webpush:subscription'

export interface StoredSubscription {
    endpoint: string
    // The VAPID public key it was created with; announce rotating its key
    // silently breaks every subscription made with the old one.
    publicKey: string
    syncedAt: number
}

export const readStoredSubscription = (): StoredSubscription | null => {
    try {
        const raw = window.localStorage.getItem(STORED_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return parsed?.endpoint ? (parsed as StoredSubscription) : null
    } catch {
        return null
    }
}

export const writeStoredSubscription = (value: StoredSubscription | null) => {
    try {
        if (value) window.localStorage.setItem(STORED_KEY, JSON.stringify(value))
        else window.localStorage.removeItem(STORED_KEY)
    } catch {
        /* best effort */
    }
}

/** VAPID keys travel as base64url; PushManager wants the raw bytes. */
export const urlBase64ToUint8Array = (base64: string): Uint8Array => {
    const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
        .replace(/-/g, '+')
        .replace(/_/g, '/')
    const raw = window.atob(padded)
    const bytes = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
    return bytes
}

export const arrayBufferToUrlBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i += 1)
        binary += String.fromCharCode(bytes[i])
    return window
        .btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

// ── opening a notification ─────────────────────────────────────────────────
//
// The service worker opens the notification's page with `pn` (announce's
// message id) and `pc` (category) appended, so a cold start can still say
// which notification brought the person in. The app strips them again.

export interface PushOpen {
    messageId: number | null
    category: string
    cleanPath: string
}

export const readPushOpen = (
    pathname: string,
    search: string
): PushOpen | null => {
    const params = new URLSearchParams(search)
    if (!params.has('pn') && !params.has('pc')) return null
    const id = Number(params.get('pn'))
    const category = params.get('pc') || ''
    params.delete('pn')
    params.delete('pc')
    const rest = params.toString()
    return {
        messageId: Number.isInteger(id) && id > 0 ? id : null,
        category,
        cleanPath: rest ? `${pathname}?${rest}` : pathname,
    }
}
